/**
 * Sign in to the Dialer: /portal/dialer/login.
 *
 * The one link a calling assistant keeps. It signs them in to eMerge and
 * lands them on the phone, never on another workspace, whatever the
 * server would otherwise pick as their home.
 *
 * Two steps, the same two the portal's own sign-in has: email and password,
 * then the code from their authenticator app (workforce accounts always
 * have one). It uses the same `login` the portal's sign-in uses, so nothing
 * about who may sign in or how is decided here.
 *
 * On success the page reloads into /portal/dialer rather than navigating
 * inside the app. Two auth states live side by side in this app (see
 * contexts/MIGRATION.md); a reload lets both read the new session, and the
 * phone starts from a clean page.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { useUser } from '../../contexts/UserContext';
import './dialer.css';

export const DIALER_PATH = '/portal/dialer';
export const DIALER_LOGIN_PATH = '/portal/dialer/login';

type Step = 'password' | 'code';

/** The controls a failure can be about. Also their DOM ids. */
type FieldId = 'dl-email' | 'dl-password' | 'dl-code';

/**
 * A failure, and which controls it is actually about.
 *
 * The alert was already announced, but nothing pointed at it: tabbing back
 * into the password found an ordinary, apparently-fine input with no hint
 * that it was the thing being complained about. `fields` is what earns the
 * `aria-describedby` and the `aria-invalid`.
 *
 * It is deliberately empty for failures that are not about a field. A rate
 * limit, a server fault or a deactivated account does not make the password
 * you typed invalid, and marking it so would be a lie told in ARIA.
 */
type FormError = { message: string; fields: readonly FieldId[] };

/** One stable id, so the association survives re-renders. */
const ERROR_ID = 'dl-signin-error';

/** Seam for tests: jsdom cannot navigate. */
export const dialerLoginNav = {
  open: (path: string) => window.location.replace(path),
};

const RATE_LIMITED =
  'Too many sign-in attempts from this network. Wait 15 minutes and try again. Your password has not changed.';

export default function DialerLogin(): JSX.Element {
  const { login, isAuthenticated, isLoading, user } = useUser();

  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FormError | null>(null);
  const [done, setDone] = useState(false);
  const [focusId, setFocusId] = useState<FieldId | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  // Where focus was when the current request began. See the effect below.
  const resumeRef = useRef<HTMLElement | null>(null);

  /**
   * Focus moves because we decided it moves.
   *
   * It used to move only when the step changed, so a failed attempt left the
   * keyboard on the submit button with the thing to correct several
   * shift-tabs away — and a screen reader heard the alert without ever being
   * taken to the field. Each site below names its target, so the reason for
   * every move is readable where the failure is handled.
   *
   * A target is consumed only once the form is idle. While a request is in
   * flight the fields are disabled, and focusing a disabled input silently
   * does nothing — a target spent mid-request would simply be lost. And it is
   * a layout effect, so focus lands in the same commit that shows the field
   * rather than after paint, when the cursor could still be on the button.
   * Both of those are #180's fix to this page, kept here.
   *
   * With no target named, focus should stay where it was — but disabling the
   * form is what moves it: a browser drops focus from a disabled control onto
   * the page itself. So a rate limit or an account refusal used to leave a
   * keyboard user at the top of the document. Where focus was when the
   * request began is remembered, and restored if focus was lost. jsdom never
   * drops focus, which is why this was found by the render harness and not
   * by the unit suite.
   */
  useLayoutEffect(() => {
    if (busy) return;
    if (focusId) {
      const target =
        focusId === 'dl-email' ? emailRef.current
          : focusId === 'dl-password' ? passwordRef.current
            : codeRef.current;
      resumeRef.current = null;
      target?.focus();
      setFocusId(null);
      return;
    }
    const back = resumeRef.current;
    resumeRef.current = null;
    const lost = !document.activeElement || document.activeElement === document.body;
    if (back && back.isConnected && lost) back.focus();
  }, [focusId, busy]);

  useEffect(() => {
    // Desktop only: on a touch device, focusing on load throws the keyboard up
    // over the form before the person has decided to type. `pointer: fine` is
    // true for a mouse or trackpad. When we cannot tell, we do not intrude.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    emailRef.current?.focus();
  }, []);

  // Already signed in (a bookmark opened in a signed-in browser): go to the
  // phone. Not while this page is signing someone in: `login` marks the
  // session authenticated before it returns, and the reload below owns that.
  if (!busy && !done && !isLoading && isAuthenticated) {
    // Carries where they were going, so verification returns them to the Dialer
    // rather than the investor portal — this page promises the bookmark always
    // opens the Dialer, and without this that promise is false.
    if (user && !user.email_verified) {
      return <Navigate to="/verify-email-required" state={{ from: { pathname: DIALER_PATH } }} replace />;
    }
    return <Navigate to={DIALER_PATH} replace />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (step === 'password' && (!email.trim() || !password)) {
      // Only the empty ones are invalid — flagging a filled field because its
      // neighbour is blank sends people to check something that is fine.
      const missing: FieldId[] = [];
      if (!email.trim()) missing.push('dl-email');
      if (!password) missing.push('dl-password');
      setError({ message: 'Enter your email and password.', fields: missing });
      setFocusId(missing[0] ?? 'dl-email');
      return;
    }
    const totp = code.replace(/\D/g, '');
    if (step === 'code' && totp.length !== 6) {
      setError({ message: 'Enter the 6-digit code from your authenticator app.', fields: ['dl-code'] });
      setFocusId('dl-code');
      return;
    }

    resumeRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setBusy(true);
    try {
      const result = await login(email.trim(), password, step === 'code' ? totp : undefined);
      switch (result.kind) {
        case 'requires2FA':
          setStep('code');
          setFocusId('dl-code');
          break;
        case 'success':
          setDone(true);
          dialerLoginNav.open(DIALER_PATH);
          return;
        case 'rateLimited':
          // Not about a field: the password may well be correct. No aria-invalid,
          // and focus stays put — there is nothing here to correct.
          setError({ message: RATE_LIMITED, fields: [] });
          break;
        case 'accountState':
          // The server's own reviewed sentence — "This account has not completed
          // setup…", "Account has been deactivated". These used to be discarded
          // and replaced with "your password is wrong", which is both untrue and
          // the opposite of actionable.
          setError({ message: result.message, fields: [] });
          break;
        case 'offline':
          setError({ message: 'Sign-in didn’t go through. Check your connection and try again.', fields: [] });
          break;
        case 'serverError':
          setError({
            message: 'Something went wrong on our side. Your password is fine — try again in a moment.',
            fields: [],
          });
          break;
        case 'invalidCredentials':
        default:
          if (step === 'code') {
            setCode('');
            setError({
              message: 'That code didn’t work. Codes change every 30 seconds; enter the one showing now.',
              fields: ['dl-code'],
            });
            setFocusId('dl-code');
          } else {
            // The server does not say which of the two was wrong, and the copy
            // asks the person to check both — so both carry the description.
            // Focus lands on the password: it is the one people retype.
            setError({
              message: 'That email and password don’t match. Check both and try again.',
              fields: ['dl-email', 'dl-password'],
            });
            setFocusId('dl-password');
          }
      }
    } catch {
      setError({ message: 'Sign-in didn’t go through. Check your connection and try again.', fields: [] });
    }
    setBusy(false);
  };

  const back = () => {
    setStep('password');
    setCode('');
    setPassword('');
    setError(null);
    // "Use a different account" is about the account, and the account is the
    // email — so land there, not on the password we just cleared.
    setFocusId('dl-email');
  };

  const describedBy = (id: FieldId) => (error?.fields.includes(id) ? ERROR_ID : undefined);
  const invalid = (id: FieldId) => (error?.fields.includes(id) ? true : undefined);

  return (
    <div className="dl">
      <header className="dl-bar">
        <div className="dl-bar-inner">
          <div className="dl-brand">
            <span className="dl-mark" aria-hidden="true">e</span>
            <div>
              <div className="dl-org">eMerge</div>
              <div className="dl-title">Dialer</div>
            </div>
          </div>
        </div>
      </header>
      <main className="dl-main">
        <section className="dl-card dl-wide dl-signin" aria-labelledby="dl-signin-h">
          <div className="dl-card-head">
            <h1 id="dl-signin-h" className="dl-signin-h">
              {step === 'password' ? 'Sign in to the Dialer' : 'Enter your code'}
            </h1>
            <p className="dl-quiet">
              {step === 'password'
                ? 'Use your eMerge email and password.'
                : <>Open your authenticator app and enter the 6-digit code for eMerge.</>}
            </p>
          </div>

          {(isLoading && !busy && !done) ? (
            <p className="dl-quiet" role="status">Checking whether you’re already signed in…</p>
          ) : done ? (
            <p role="status"><strong>Signed in. Opening the Dialer…</strong></p>
          ) : (
            <form className="dl-form" onSubmit={submit} noValidate>
              {step === 'password' ? (
                <>
                  <div className="dl-field">
                    <label htmlFor="dl-email">Email</label>
                    <input
                      id="dl-email"
                      ref={emailRef}
                      type="email"
                      autoComplete="username"
                      inputMode="email"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={busy}
                      aria-invalid={invalid('dl-email')}
                      aria-describedby={describedBy('dl-email')}
                    />
                  </div>
                  <div className="dl-field">
                    <div className="dl-field-top">
                      <label htmlFor="dl-password">Password</label>
                      <Link className="dl-link dl-small-link" to="/forgot-password">Forgot password?</Link>
                    </div>
                    <input
                      id="dl-password"
                      ref={passwordRef}
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={busy}
                      aria-invalid={invalid('dl-password')}
                      aria-describedby={describedBy('dl-password')}
                    />
                  </div>
                </>
              ) : (
                <div className="dl-field">
                  <label htmlFor="dl-code">Authenticator code</label>
                  <input
                    id="dl-code"
                    ref={codeRef}
                    className="dl-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))}
                    disabled={busy}
                    aria-invalid={invalid('dl-code')}
                    aria-describedby={describedBy('dl-code')}
                  />
                  <p className="dl-quiet">Signing in as <strong>{email.trim()}</strong>.</p>
                  <p className="dl-quiet">
                    Lost your phone? <Link className="dl-link" to="/login">Sign in on the main page</Link> and
                    choose &ldquo;Use a backup code&rdquo; to register a new one.
                  </p>
                </div>
              )}

              {error && <p id={ERROR_ID} className="dl-alert" role="alert">{error.message}</p>}

              <button type="submit" className="dl-btn dl-primary dl-big" disabled={busy}>
                {busy ? 'Signing in…' : step === 'password' ? 'Continue' : 'Sign in'}
              </button>
              {step === 'code' && (
                <button type="button" className="dl-link dl-center" onClick={back} disabled={busy}>
                  Use a different account
                </button>
              )}
            </form>
          )}

          <div className="dl-signin-notes">
            <p>
              <strong>New here?</strong> Set up your account from the invitation email eMerge sent you
              first, then come back to this page.
            </p>
            <p>
              After you sign in, the phone asks for your <strong>Amazon Connect</strong> username and
              password. Those are separate from your eMerge sign-in.
            </p>
            <p>Bookmark this page. It always opens the Dialer.</p>
          </div>
        </section>
      </main>
    </div>
  );
}
